import type { LegendPayload } from 'recharts/types/component/DefaultLegendContent';

import { ChartLegendContent } from '@/lib/base/foundations/charts-base';
import type { ChartDatapointSelection, ChartType } from '@/types/reports';

import type { ChartConfiguration, LineChartOptions } from '../types';
import { computeValueAxisMeta } from '../utils/chartAxisMeta';
import type { MeasureUnitGroup } from '../utils/measureUnits';
import { AreaChartCase } from './AreaChartCase';
import { BarChartCase } from './BarChartCase';
import { LineChartCase } from './LineChartCase';
import { FACET_MIN_PANEL_HEIGHT, FACET_YAXIS_WIDTH } from './shared';

export interface FacetedCartesianChartProps {
  chartType: ChartType;
  /** Which chart-case component renders each panel. Line routes to 'area'
   * when its areaFill option is not 'none' — decided by the caller. */
  effectiveRenderer: 'bar' | 'line' | 'area';
  unitGroups: MeasureUnitGroup[];
  chartData: Record<string, unknown>[];
  config: ChartConfiguration;
  columnTitles: Record<string, string>;
  columnTypes: Record<string, string>;
  currency: string;
  seriesColors: Record<string, string>;
  height: number;
  legendAlign: 'left' | 'center' | 'right';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Formatter type is complex, using any for callback
  tooltipFormatter: (value: any, name: any, entry: any) => any;
  categoryBottomLabel?: string;
  /** Unique-per-instance id prefix, also used to derive a shared syncId. */
  gradientPrefix: string;
  /** Required when effectiveRenderer is 'line' or 'area'. */
  lineOptions?: LineChartOptions;
  /** Called when the user clicks (or keyboard-activates) a chart datapoint. */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
  /** The currently active/selected datapoint, used to dim all others. */
  activeSelection?: ChartDatapointSelection | null;
}

/**
 * Renders one vertically-stacked panel per unit group for a cartesian
 * (bar/line/area) chart whose measures span more than one unit — each
 * panel gets its own y-scale, all panels share the x-axis, and a single
 * legend covering every series renders once above the panels.
 */
export function FacetedCartesianChart({
  chartType,
  effectiveRenderer,
  unitGroups,
  chartData,
  config,
  columnTitles,
  columnTypes,
  currency,
  seriesColors,
  height,
  legendAlign,
  tooltipFormatter,
  categoryBottomLabel,
  gradientPrefix,
  lineOptions,
  onDatapointSelect,
  activeSelection,
}: FacetedCartesianChartProps) {
  const groupCount = Math.max(unitGroups.length, 1);
  const panelHeight = Math.max(
    FACET_MIN_PANEL_HEIGHT,
    Math.floor(height / groupCount)
  );
  const syncId = `facet-${gradientPrefix}`;

  const allSeriesKeys = unitGroups.flatMap(group => group.keys);
  const legendPayload: LegendPayload[] = allSeriesKeys.map(key => ({
    value: columnTitles[key] || key,
    color: seriesColors[key],
    type: 'circle' as const,
  }));
  const ariaLabel = `${chartType} chart, split by measurement unit into ${unitGroups.length} panels: ${allSeriesKeys
    .map(key => columnTitles[key] || key)
    .join(', ')}`;

  return (
    <div className='flex flex-col gap-2' role='group' aria-label={ariaLabel}>
      {config.legend.show && (
        <ChartLegendContent
          payload={legendPayload}
          align={legendAlign}
          layout={config.legend.layout}
        />
      )}
      {unitGroups.map((group, index) => {
        const isLastPanel = index === unitGroups.length - 1;
        const axisMeta = computeValueAxisMeta(
          chartData,
          group.keys,
          columnTypes,
          config,
          currency
        );
        const valueAxisTitle =
          group.keys.length === 1 ? columnTitles[group.keys[0]] : undefined;

        const panelProps = {
          chartData,
          config,
          columnTitles,
          height: panelHeight,
          legendAlign,
          tooltipFormatter,
          seriesKeys: group.keys,
          seriesColors,
          axisMeta,
          categoryBottomLabel,
          valueAxisTitle,
          showXAxisLabels: isLastPanel,
          yAxisWidth: FACET_YAXIS_WIDTH,
          renderLegend: false,
          syncId,
          onDatapointSelect,
          activeSelection,
        };

        if (effectiveRenderer === 'bar') {
          return <BarChartCase key={group.unit} {...panelProps} />;
        }
        if (effectiveRenderer === 'area') {
          return (
            <AreaChartCase
              key={group.unit}
              {...panelProps}
              lineOptions={lineOptions as LineChartOptions}
              gradientPrefix={gradientPrefix}
            />
          );
        }
        return (
          <LineChartCase
            key={group.unit}
            {...panelProps}
            lineOptions={lineOptions as LineChartOptions}
          />
        );
      })}
    </div>
  );
}
